import { getCombinedMatchedMetadataFromLabels } from '@modules/feedback/components/FeedbackDetails/utils/getCombinedMatchedMetadataFromLabels';
import { alpha, Box, Tooltip } from '@mui/material';
import { useEffect } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
} from 'v2/core/entities/feedback';
import { useGetUser } from 'v2/core/services/user';
import { trackFeedbackEvents } from '../../utils';
import { FeedbackDetailsConversation } from '../FeedbackDetails/FeedbackDetailsConversation';

interface FeedbackConversationPreviewProps {
  feedback: Feedback;
  setIsFeedbackDetailsDrawerVisible: (isVisible: boolean) => void;
  selectedLabels?: FeedbackLabel[];
  disableClickToExpand?: boolean;
  enableScrollView?: boolean;
  shouldShowSavedFeedbackCard?: boolean;
}

const FeedbackConversationPreview = ({
  feedback,
  setIsFeedbackDetailsDrawerVisible,
  selectedLabels = [],
  disableClickToExpand,
  enableScrollView,
  shouldShowSavedFeedbackCard,
}: FeedbackConversationPreviewProps) => {
  const {
    tenant: {
      dashboardSettings: { showUnTranslatedRecords = false },
    },
  } = useGetUser();
  useEffect(() => {
    // If there are no selected labels, then we don't need to do anything as
    // there is nothing to highlight.
    if (!selectedLabels.length) {
      return undefined;
    }
    const firstSelectedLabelID = selectedLabels?.[0]?.id ?? '';

    const firstSelectedLabelMatchMsgIndex =
      getCombinedMatchedMetadataFromLabels(selectedLabels)
        .filter((metadata) => metadata.id === firstSelectedLabelID)
        .sort((a, b) => (a.msgIDx ?? 0) - (b.msgIDx ?? 0))?.[0]?.msgIDx ?? 0;

    const scrollElId =
      firstSelectedLabelID + firstSelectedLabelMatchMsgIndex.toString();

    if (scrollElId) {
      const scrollEl = document.querySelectorAll(
        `#feedback-preview-${feedback.id} [data-highlight-id="${scrollElId}"]`,
      )[0];

      if (scrollEl) {
        scrollIntoView(scrollEl, {
          block: 'start',
          // We need to add a boundary so that the scroll of the page doesn't
          // get affected.
          boundary: document.getElementById(
            `feedback-preview-container-${feedback.id}`,
          ),
        });
      }
    }
  }, [selectedLabels, feedback.id]);

  const shouldAddBackground =
    feedback.content.type !== FeedbackContentType.Audio &&
    feedback.content.type !== FeedbackContentType.Review;

  return (
    <Tooltip
      title={!disableClickToExpand ? 'Click to expand' : ''}
      placement="top"
      arrow
      PopperProps={{
        disablePortal: true,
      }}
    >
      <Box
        onClick={() => {
          if (disableClickToExpand) {
            return;
          }
          setIsFeedbackDetailsDrawerVisible(true);
          trackFeedbackEvents({
            eventName: 'Feedback Card Expanded',
            feedbackID: feedback.id,
          });
        }}
        sx={{
          maxHeight: '360px',
          overflow: enableScrollView ? 'auto' : 'hidden',
          border: '1.5px solid',
          borderRadius: 2.5,
          p: shouldAddBackground ? 2 : 0,
          my: shouldAddBackground ? 0 : 2,
          borderColor: shouldShowSavedFeedbackCard
            ? (theme) => theme.palette.background.paper
            : 'transparent',
          outline: shouldShowSavedFeedbackCard ? '2px solid' : undefined,
          outlineColor: shouldShowSavedFeedbackCard
            ? (theme) => theme.palette.background.paper
            : undefined,
          boxShadow: 'none',
          backgroundColor: shouldAddBackground ? 'feedback.previewBg' : 'none',
          ':hover': !disableClickToExpand
            ? {
                cursor: 'pointer',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
              }
            : {},
          userSelect: 'none',
          '[data-highlight-id]': {
            pointerEvents: 'none',
          },
        }}
        id={`feedback-preview-container-${feedback.id}`}
      >
        <Box width="100%" id={`feedback-preview-${feedback.id}`}>
          <FeedbackDetailsConversation
            feedback={feedback}
            selectedLabels={selectedLabels}
            variant="preview"
            showOriginalLanguage={showUnTranslatedRecords}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};

export default FeedbackConversationPreview;
